import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, AlertTriangle, MinusCircle, RefreshCw, ExternalLink, ArrowLeft } from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { githubApi } from '@/services/githubApi';
import { cn } from '@/lib/utils';

interface WorkflowRun {
  id: number;
  name: string;
  status: string;
  conclusion: string | null;
  html_url: string;
  created_at: string;
  head_branch: string;
}

type StatusKind = 'success' | 'failure' | 'running' | 'pending' | 'cancelled';

function statusKindFor(run: WorkflowRun): StatusKind {
  if (run.status !== 'completed') {
    return run.status === 'in_progress' ? 'running' : 'pending';
  }
  switch (run.conclusion) {
    case 'success': return 'success';
    case 'failure': case 'timed_out': return 'failure';
    default: return 'cancelled';
  }
}

function StatusIcon({ kind }: { kind: StatusKind }) {
  switch (kind) {
    case 'success': return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
    case 'failure': return <XCircle className="h-4 w-4 text-red-600" />;
    case 'running': return <Spinner size="sm" aria-label="Running" />;
    case 'pending': return <Clock className="h-4 w-4 text-amber-500" />;
    case 'cancelled': return <MinusCircle className="h-4 w-4 text-slate-400" />;
  }
}

export default function CiStatusPage() {
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await githubApi.listWorkflowRuns();
      setRuns(data);
    } catch {
      setError('Failed to load CI status. Ensure GitHub is connected.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  return (
    <div className="p-8">
      <Link to="/verify" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Verify
      </Link>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">CI/CD Status</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Latest GitHub Actions workflow runs across connected repositories.
          </p>
        </div>
        <button
          onClick={loadRuns}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
        >
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        </div>
      )}

      {!loading && runs.length === 0 && !error ? (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 text-center">
          <Clock className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-700" />
          <h2 className="mt-4 font-semibold text-slate-900 dark:text-slate-100">No workflow runs found</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
            Connect a GitHub organization and ensure repositories have workflows configured.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {runs.map((run) => {
            const kind = statusKindFor(run);
            return (
              <a
                key={run.id}
                href={run.html_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-colors"
              >
                <StatusIcon kind={kind} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{run.name}</p>
                  <p className="text-xs text-slate-500">{run.head_branch}</p>
                </div>
                <span className="text-xs text-slate-400">
                  {new Date(run.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
                <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
